// lib/nextUrl.js
// Where to send someone after they sign in.
//
// This used to travel in router state (`navigate("/login", { state: { from } })`),
// which loses it in every case that matters: a hard navigation, a refresh of
// /login, and the whole Google round-trip. It travels in the URL now, as
// ?next=<encoded path>, so it survives all three.
//
// A redirect target that arrives from a URL is attacker-controlled input: a
// crafted link is how a login page becomes an open redirect. So nothing is
// trusted, and anything that is not plainly a path on this site falls back to
// the same safe default.

export const FALLBACK_NEXT = "/courses";

// Any origin will do here: it exists only so the URL parser has something to
// resolve a relative path against, and is never navigated to.
const PROBE_ORIGIN = "https://scriptedcad.invalid";

// A value is usable only if it is a path on this site.
export function isSafeNext(raw) {
  if (typeof raw !== "string") return false;
  if (raw.length === 0 || raw.length > 2048) return false;

  // Exactly one leading slash. "//host" is protocol-relative, and browsers
  // treat "/\host" the same way, so both of those leave the site.
  if (raw[0] !== "/") return false;
  if (raw[1] === "/" || raw[1] === "\\") return false;

  // Control characters and backslashes are how a host gets smuggled past a
  // check like the one above: browsers strip or rewrite them, so what gets
  // validated is not what gets navigated.
  for (let i = 0; i < raw.length; i += 1) {
    const code = raw.charCodeAt(i);
    // C0 controls, DEL, and the backslash itself. Written as codes rather
    // than a character class so the control characters are not smuggled
    // through a regex literal (which eslint rightly objects to).
    if (code <= 0x1f || code === 0x7f || code === 0x5c) return false;
  }

  // Backstop: resolve it with the browser's own parser and insist it stayed
  // put. This catches the tricks the rules above have not thought of, and it
  // covers any scheme, since an absolute URL resolves to its own origin.
  try {
    if (new URL(raw, PROBE_ORIGIN).origin !== PROBE_ORIGIN) return false;
  } catch {
    return false;
  }

  return true;
}

// The value to act on: the target if it is safe, the default if it is not.
export const sanitizeNext = (raw) => (isSafeNext(raw) ? raw : FALLBACK_NEXT);

// Where the user is now, in the form that goes into ?next=. Takes a router
// location, or defaults to the real one for the few callers outside the Router.
export function currentPath(location = window.location) {
  return `${location.pathname}${location.search || ""}${location.hash || ""}`;
}

// The login URL that comes back to `from` (defaults to where the user is).
export function loginUrlFor(from) {
  const target = from == null ? currentPath() : from;
  if (!isSafeNext(target)) return "/login";
  return `/login?next=${encodeURIComponent(target)}`;
}

// Carries ?next= across the login/register switch, so bouncing between the two
// does not lose where the user was headed.
export function withNextFrom(search, path) {
  const next = new URLSearchParams(search).get("next");
  return isSafeNext(next) ? `${path}?next=${encodeURIComponent(next)}` : path;
}

// Reads ?next= off a query string, already sanitized.
export function readNext(search) {
  return sanitizeNext(new URLSearchParams(search).get("next"));
}

// ── The Google round-trip ────────────────────────────────────────────────────
// Google sends the user back to a URL the backend builds
// (`/login?token=...&name=...&email=...`, AuthController.GoogleCallback), and it
// does not echo anything of ours: the `state` it hands Google is the frontend
// URL and the callback never reads it back. So ?next= cannot survive the trip in
// the URL, and this is frontend-only work.
//
// sessionStorage does survive it: same tab, same origin on return, and scoped to
// the tab so a second tab cannot pick up a stale destination. If it is missing
// (the consent screen was completed in another tab) the fallback applies.
const STASH_KEY = "sc_next_after_sso";

export function stashNext(target) {
  if (!isSafeNext(target)) return;
  try {
    sessionStorage.setItem(STASH_KEY, target);
  } catch {
    // Private mode, blocked storage: the fallback is correct behaviour, not an
    // error worth surfacing.
  }
}

export function takeStashedNext() {
  try {
    const value = sessionStorage.getItem(STASH_KEY);
    sessionStorage.removeItem(STASH_KEY);
    return isSafeNext(value) ? value : null;
  } catch {
    return null;
  }
}
